import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { API_BASE_URL } from "../utils/config";

// Standalone public page — no login, no DashboardLayout/Sidebar (those
// require an authenticated session). Only status/badge logic is duplicated
// from LoanTransactions.jsx so this page has no dependency on auth-gated code.
function statusBadge(loan) {
  if (loan.status === "PENDING") {
    return <span className="badge badge-warning">Menunggu</span>;
  }
  if (loan.status === "REJECTED") {
    return <span className="badge badge-danger">Ditolak</span>;
  }
  if (loan.status === "RETURNED") {
    return <span className="badge badge-success">Dikembalikan</span>;
  }
  if (loan.status === "RETURNING") {
    return <span className="badge badge-warning">Minta Kembali</span>;
  }
  if (loan.is_overdue) {
    return <span className="badge badge-danger">Terlambat {Math.abs(loan.days_until_due)} hari</span>;
  }
  if (loan.days_until_due === 0) {
    return <span className="badge badge-danger">Jatuh tempo hari ini</span>;
  }
  if (loan.days_until_due === 1) {
    return <span className="badge badge-warning">Jatuh tempo besok</span>;
  }
  return <span className="badge badge-info">{loan.days_until_due} hari lagi</span>;
}

export default function SharedLoanRekap() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Link tidak valid.");
      setLoading(false);
      return;
    }
    fetch(`${API_BASE_URL}/api/loan-transactions/shared/?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.detail || "Link tidak valid.");
        }
        return r.json();
      })
      .then((data) => setLoans(Array.isArray(data) ? data : []))
      .catch((e) => setError(e.message || "Link sudah kedaluwarsa atau tidak valid."))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 sm:p-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-xl font-semibold text-strong mb-1">Daftar Peminjaman</h1>
        <p className="text-sm text-muted mb-6">Rekap barang yang sedang/pernah dipinjam.</p>

        {loading && <p className="text-sm text-muted">Memuat…</p>}
        {!loading && error && (
          <div className="glass-card p-4 text-sm text-red-500">{error}</div>
        )}

        {!loading && !error && loans.length === 0 && (
          <div className="glass-card p-4 text-sm text-muted">Tidak ada data peminjaman.</div>
        )}

        {!loading && !error && loans.length > 0 && (
          <div className="glass-table-wrap overflow-x-auto">
            <table className="glass-table">
              <thead>
                <tr>
                  <th className="text-left">Referensi</th>
                  <th className="text-left">Barang</th>
                  <th className="!text-right">Qty</th>
                  <th className="text-left">PIC</th>
                  <th className="text-left">Pengaju</th>
                  <th className="text-left">Jatuh Tempo</th>
                  <th className="text-left">Status</th>
                  <th className="text-left">Tgl Pinjam</th>
                </tr>
              </thead>
              <tbody>
                {loans.map((loan) => (
                  <tr key={loan.id}>
                    <td className="px-4 py-2 font-mono text-xs">{loan.reference_code}</td>
                    <td className="px-4 py-2">
                      <span className="font-mono text-brand-600 dark:text-brand-400 text-xs mr-1">{loan.item_code}</span>
                      {loan.item_name}
                    </td>
                    <td className="px-4 py-2 text-right">{loan.quantity} {loan.item_unit}</td>
                    <td className="px-4 py-2">{loan.pic_name}</td>
                    <td className="px-4 py-2 text-muted">{loan.created_by_username || "-"}</td>
                    <td className="px-4 py-2">{loan.due_date}</td>
                    <td className="px-4 py-2">{statusBadge(loan)}</td>
                    <td className="px-4 py-2 text-xs text-muted">
                      {loan.created_at ? new Date(loan.created_at).toLocaleString("id-ID") : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
